import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Copy, Check, Mail, Phone, ExternalLink, Shield, Loader2, Info } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Templates de mensagem educada e neutra (pt-BR)
const buildDefaultTemplate = ({ gabineteNome, despesa, cidadao }) => {
  const me = cidadao?.nome || 'um cidadão';
  const greet = `Prezado(a) ${gabineteNome || 'Parlamentar'},`;
  const intro = `Sou ${me}, eleitor(a) interessado(a) no acompanhamento dos gastos públicos e da atuação parlamentar.`;

  if (despesa) {
    const val = typeof despesa.valor === 'number'
      ? despesa.valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
      : '';
    const desc = despesa.descricao || despesa.categoria || 'uma despesa registrada';
    const forn = despesa.fornecedor ? ` junto ao fornecedor ${despesa.fornecedor}` : '';
    const data = despesa.data ? ` em ${despesa.data}` : '';
    return [
      greet,
      '',
      intro,
      '',
      `Consultando os dados públicos da Câmara dos Deputados, observei o registro de ${desc}${forn}, no valor de ${val}${data}.`,
      '',
      'Gostaria, respeitosamente, de solicitar esclarecimentos sobre:',
      '• A finalidade específica deste gasto;',
      '• O contexto em que a despesa foi realizada;',
      '• Quais resultados ou entregas estão associados a ela.',
      '',
      'Acredito que a transparência fortalece a confiança entre representantes e representados, e desde já agradeço pela atenção.',
      '',
      'Atenciosamente,',
      cidadao?.nome || '[Seu nome]',
    ].join('\n');
  }

  return [
    greet,
    '',
    intro,
    '',
    'Gostaria, respeitosamente, de solicitar informações sobre a atuação parlamentar do gabinete em temas relevantes para o(a) eleitor(a) e sobre os gastos declarados da cota parlamentar.',
    '',
    'Acredito que a transparência fortalece a confiança entre representantes e representados, e desde já agradeço pela atenção.',
    '',
    'Atenciosamente,',
    cidadao?.nome || '[Seu nome]',
  ].join('\n');
};

const buildSubject = ({ despesa }) => {
  if (despesa) {
    const val = typeof despesa.valor === 'number'
      ? despesa.valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
      : '';
    return `Esclarecimento sobre despesa registrada (${val || 'cota parlamentar'})`;
  }
  return 'Solicitação de esclarecimento (cidadão)';
};

/**
 * Modal de Contato e Cobrança Cidadã.
 * Props:
 *  - open, onClose
 *  - deputadoId (int) — obrigatório para buscar canais oficiais
 *  - despesa (opcional) — { descricao, categoria, fornecedor, valor, data }
 *  - cidadaoNome (opcional) — nome pré-preenchido
 */
const CitizenContactModal = ({ open, onClose, deputadoId, despesa, cidadaoNome }) => {
  const [contato, setContato] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cidadao, setCidadao] = useState({ nome: cidadaoNome || '' });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open || !deputadoId) return;
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/legislative/deputado/${deputadoId}/contato`, { timeout: 20000 })
      .then(r => { if (!cancelled) setContato(r.data); })
      .catch(() => { if (!cancelled) setContato({ error: 'Falha ao carregar canal oficial.' }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, deputadoId]);

  const subject = useMemo(() => buildSubject({ despesa }), [despesa]);
  const [body, setBody] = useState('');

  useEffect(() => {
    if (!contato) return;
    setBody(buildDefaultTemplate({
      gabineteNome: contato.nome,
      despesa,
      cidadao: { nome: cidadao.nome },
    }));
  }, [contato, despesa, cidadao.nome]);

  const mailtoHref = useMemo(() => {
    if (!contato?.email) return null;
    const params = new URLSearchParams({ subject, body });
    return `mailto:${contato.email}?${params.toString()}`;
  }, [contato, subject, body]);

  const copyBody = async () => {
    try {
      await navigator.clipboard.writeText(`Para: ${contato?.email || ''}\nAssunto: ${subject}\n\n${body}`);
      setCopied(true);
      toast.success('Mensagem copiada!');
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error('Não foi possível copiar. Selecione o texto manualmente.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="citizen-contact-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Mail className="w-5 h-5 text-[#006030]" />
            Contato e Cobrança Cidadã
          </DialogTitle>
          <DialogDescription className="text-xs text-[#475569]">
            Fale diretamente com o gabinete via canal público oficial. A plataforma apenas facilita o acesso. A mensagem é enviada pelo seu próprio e-mail.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-5 h-5 animate-spin text-[#006030] mr-2" />
            <span className="text-sm text-[#94A3B8]">Carregando canais oficiais...</span>
          </div>
        )}

        {!loading && contato?.error && (
          <div className="p-3 bg-[#FFF8E1] border border-[#F0D000] rounded-md text-xs text-[#8C5A00]">
            {contato.error}
          </div>
        )}

        {!loading && contato && !contato.error && (
          <>
            {/* Canais oficiais */}
            <div className="p-3 bg-[#F4F6F8] border border-[#E2E8F0] rounded-md space-y-2" data-testid="canais-oficiais">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-[#2E7D32]" />
                <span className="text-[10px] font-bold text-[#2E7D32] uppercase tracking-wider">
                  Canais oficiais · {contato.fonte}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                {contato.email && (
                  <div className="flex items-center gap-2">
                    <Mail className="w-3 h-3 text-[#475569]" />
                    <a href={`mailto:${contato.email}`} className="text-[#006030] hover:underline break-all" data-testid="contato-email">
                      {contato.email}
                    </a>
                  </div>
                )}
                {contato.telefone && (
                  <div className="flex items-center gap-2">
                    <Phone className="w-3 h-3 text-[#475569]" />
                    <span className="text-[#0F1C2E]">{contato.telefone}</span>
                  </div>
                )}
                {contato.gabinete_nome && (
                  <div className="text-[11px] text-[#475569] col-span-full">
                    Gabinete: {contato.gabinete_nome}
                    {contato.sala && ` · Sala ${contato.sala}`}
                    {contato.predio && ` · ${contato.predio}`}
                    {contato.andar && ` · ${contato.andar}º andar`}
                  </div>
                )}
                {contato.pagina_camara && (
                  <a
                    href={contato.pagina_camara}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[11px] text-[#006030] hover:underline flex items-center gap-1 col-span-full"
                  >
                    Página oficial na Câmara <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </div>

            {/* Contexto da cobrança (se houver despesa) */}
            {despesa && (
              <div className="p-3 bg-[#FFF8E1] border border-[#F0D000] rounded-md text-xs">
                <div className="flex items-center gap-1 mb-1">
                  <Info className="w-3 h-3 text-[#8C5A00]" />
                  <span className="font-bold text-[#8C5A00]">Item em questão</span>
                </div>
                <p className="text-[#0F1C2E]">
                  <span className="font-semibold">{despesa.descricao || despesa.categoria}</span>
                  {despesa.fornecedor && <> · Fornecedor: {despesa.fornecedor}</>}
                  {typeof despesa.valor === 'number' && (
                    <> · <strong>{despesa.valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</strong></>
                  )}
                  {despesa.data && <> · {despesa.data}</>}
                </p>
              </div>
            )}

            {/* Seu nome */}
            <div>
              <Label className="text-[10px] font-bold text-[#94A3B8] uppercase tracking-wider">Seu nome (será inserido na mensagem)</Label>
              <Input
                value={cidadao.nome}
                onChange={e => setCidadao({ nome: e.target.value })}
                placeholder="Ex.: Maria Silva"
                className="mt-1 border-[#E2E8F0]"
                data-testid="contact-cidadao-nome"
              />
            </div>

            {/* Assunto */}
            <div>
              <Label className="text-[10px] font-bold text-[#94A3B8] uppercase tracking-wider">Assunto</Label>
              <Input value={subject} readOnly className="mt-1 border-[#E2E8F0] bg-[#F4F6F8]" data-testid="contact-subject" />
            </div>

            {/* Corpo editável */}
            <div>
              <Label className="text-[10px] font-bold text-[#94A3B8] uppercase tracking-wider">Mensagem (revise antes de enviar)</Label>
              <Textarea
                value={body}
                onChange={e => setBody(e.target.value)}
                rows={12}
                className="mt-1 text-xs border-[#E2E8F0] font-mono"
                data-testid="contact-body"
              />
              <p className="text-[10px] text-[#94A3B8] mt-1">
                A linguagem é neutra, educada e baseada em dados oficiais. Você pode editar livremente antes de enviar.
              </p>
            </div>

            {/* Ações */}
            <div className="flex flex-col sm:flex-row gap-2 pt-1">
              <Button
                onClick={copyBody}
                variant="outline"
                className="flex-1 border-[#006030] text-[#006030] hover:bg-[#006030] hover:text-white"
                data-testid="contact-copy-btn"
              >
                {copied ? <><Check className="w-4 h-4 mr-1" /> Copiado</> : <><Copy className="w-4 h-4 mr-1" /> Copiar mensagem</>}
              </Button>
              {mailtoHref && (
                <Button
                  asChild
                  className="flex-1 bg-[#006030] hover:bg-[#004D26] text-white"
                  data-testid="contact-mailto-btn"
                >
                  <a href={mailtoHref} target="_blank" rel="noreferrer" onClick={() => toast.success('Abrindo seu cliente de e-mail...')}>
                    <Mail className="w-4 h-4 mr-1" /> Abrir no meu e-mail
                  </a>
                </Button>
              )}
            </div>

            {/* Aviso compliance */}
            <div className="mt-2 text-[10px] text-[#94A3B8] italic border-t border-[#E2E8F0] pt-2">
              <Badge variant="outline" className="text-[9px] mr-1">Aviso</Badge>
              A plataforma não envia mensagens em massa nem em seu nome. O envio ocorre pelo seu próprio cliente de e-mail,
              diretamente ao canal oficial publicado pela Câmara dos Deputados. Mantenha uma linguagem respeitosa.
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CitizenContactModal;
